<script lang="ts">
  // The activity table's (/activities/advanced, a jQuery DataTables page) "Tags" button: applies a
  // tag to, or takes it off, every selected activity at once. A tag on all of them is ticked and
  // clicking takes it off all; one on some or none is added to all. The table owns the selection,
  // so it hands over a getter and is told what changed -- see advanced_activities.js.
  import * as Popover from '$lib/components/ui/popover';
  import ActivityTagMenuList from '@/Common/ActivityTagMenuList.svelte';
  import { activityTagOptions, allTags } from '@/Common/activityTagOptions';
  import { bulkActivityTag } from '@/Util/api';
  import { notifyApiError, showWarningToast } from '@/Util/notify';
  import { ErrorCode } from '@/Util/errorCodes';

  type SelectedActivity = { path: string, custom_tags?: string[] };

  /** How many rows are selected; the table updates it as the selection changes. */
  export let count = 0;
  export let getSelected: () => SelectedActivity[];
  /** Called with each changed activity's tags as they now stand, so the table can update its rows. */
  export let onSaved: (tags: Record<string, string[]>) => void;

  const options = activityTagOptions();
  $: offered = allTags($options);

  let open = false;
  let selection: SelectedActivity[] = [];
  let pending: string[] = [];

  // Re-read whenever the menu opens: the selection may have changed since.
  $: if (open) selection = getSelected();
  $: onAll = offered.map((t) => t.name).filter((tag) => selection.length && selection.every((a) => a.custom_tags?.includes(tag)));
  $: onSome = offered.map((t) => t.name).filter((tag) => !onAll.includes(tag) && selection.some((a) => a.custom_tags?.includes(tag)));

  async function toggle(tag: string) {
    if (pending.includes(tag) || !selection.length) return;
    const added = !onAll.includes(tag);
    pending = [...pending, tag];
    try {
      const { custom_tags, full } = await bulkActivityTag(selection.map((a) => a.path), tag, added);
      selection = selection.map((a) => (custom_tags[a.path] ? { ...a, custom_tags: custom_tags[a.path] } : a));
      onSaved(custom_tags);
      if (full.length) {
        showWarningToast(`${full.length} of the selected activities already have the most tags an activity can carry, so "${tag}" wasn't added to them.`);
      }
    } catch (error) {
      notifyApiError(error, { code: ErrorCode.ACTIVITY_TAG_UPDATE_FAILED, message: "Could not update those activities' tags." });
    } finally {
      pending = pending.filter((existing) => existing !== tag);
    }
  }
</script>

<Popover.Root portal="#shadcn-ui-dialog-target" bind:open>
  <Popover.Trigger asChild let:builder>
    <!-- Bootstrap classes to match the DataTables buttons it sits among. -->
    <button
      use:builder.action
      {...builder}
      type="button"
      class="btn btn-default"
      class:disabled={!count}
      disabled={!count}
      title="Add or remove tags on the selected activities"
    >
      <i class="fa fa-tags" aria-hidden="true"></i>
      Tags
    </button>
  </Popover.Trigger>
  <Popover.Content class="w-60 min-w-0 p-2" align="start">
    <div class="px-2 pb-1 text-xs text-muted-foreground">
      {selection.length} {selection.length === 1 ? 'activity' : 'activities'} selected
    </div>
    <ActivityTagMenuList tags={offered} selected={onAll} partial={onSome} onToggle={toggle} disabledTags={pending} />
  </Popover.Content>
</Popover.Root>
